import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  EntityResponseAuditable,
  EntityResponseAuditableODataWithMetadataResponse,
  EntityResponseWithNestedObjectsAuditable,
  GstarClient,
} from '../../../web-api-client';
import { mapEntityFromResponse } from '../../../modules/admin/mappers/entity/map-entity-from-response';
import { mapEntityFromResponseNested } from '../../../modules/admin/mappers/entity/map-entity-from-response-nested';
import { mapEntityToCreateRequest } from '../../../modules/admin/mappers/entity/map-entity-to-create-request';
import { mapEntityToUpdateRequest } from '../../../modules/admin/mappers/entity/map-entity-to-update-request';
import { Entity } from '../../../modules/portfolio/models/entity';

@Injectable({
  providedIn: 'root',
})
export class EntityService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  private reloadEntityListSubj: BehaviorSubject<void> =
    new BehaviorSubject<void>(undefined);

  entitiesByClientId: Observable<Entity[]> = combineLatest([
    toObservable(this.headerService.selectedClient),
    this.reloadEntityListSubj,
  ]).pipe(
    switchMap(([selectedClient]: [Client | undefined, void]) => {
      if (!selectedClient) return of(undefined);

      return this.gstarClient.entitiesGET(
        selectedClient.id,
        undefined,
        'isActive eq true',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );
    }),
    map((x: EntityResponseAuditableODataWithMetadataResponse | undefined) => {
      if (x && x.value) return x.value.map(y => new Entity(y));
      return [];
    })
  );

  entitiesByClientOrDefault: Observable<Entity[]> = combineLatest([
    toObservable(this.headerService.selectedClient),
    this.reloadEntityListSubj,
  ]).pipe(
    switchMap(([selectedClient]: [Client | undefined, void]) => {
      return selectedClient === undefined
        ? this.gstarClient.entitiesGET2(
            undefined,
            'isActive eq true',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
          )
        : this.gstarClient.entitiesGET(
            selectedClient.id,
            undefined,
            'isActive eq true',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
          );
    }),
    map(
      (
        response: EntityResponseAuditableODataWithMetadataResponse | undefined
      ) => {
        if (response && response.value) {
          return response.value.map(
            (entity: EntityResponseAuditable) => new Entity(entity)
          );
        }
        return [];
      }
    ),
    shareReplay(1)
  );

  query(): Observable<Entity[]> {
    return this.gstarClient
      .entitiesGET2(
        undefined,
        'isActive eq true',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(
          (x: EntityResponseAuditableODataWithMetadataResponse | undefined) => {
            if (x && x.value) return x.value.map(y => new Entity(y));
            return [];
          }
        )
      );
  }

  getEntityById(id: number): Observable<Entity> {
    return this.gstarClient
      .entitiesGET3(id)
      .pipe(
        map((response: EntityResponseWithNestedObjectsAuditable) =>
          mapEntityFromResponseNested(response)
        )
      );
  }

  reloadEntityList = (): void => this.reloadEntityListSubj.next();

  create(entity: Entity): Observable<Entity> {
    return this.gstarClient
      .entitiesPOST(mapEntityToCreateRequest(entity))
      .pipe(map(response => mapEntityFromResponse(response)));
  }

  update(id: number, entity: Entity): Observable<void> {
    return this.gstarClient.entitiesPUT(id, mapEntityToUpdateRequest(entity));
  }
}
